import React from "react";

import { Box, IconButton, Typography, Avatar, Chip } from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";

import { getRoleColor } from "../../auth/roles";
import DataTable from "../../ui/Table";
import { avatarColorOf } from "../../utils/avatarUtils";

function EmployeeTable({
  employees,
  loading,
  onEdit,
  onDelete,
  selectedIds = [],
  onSelectionChange
}) {
  const sortedEmployees = [...employees].sort(
    (a, b) => a.employee_id - b.employee_id
  );

  const columns = [
    {
      key: "employee_name",
      label: "Employee Name",
      width: "24%",
      render: (employee) => {
        const avatarSeed = employee.employee_id || employee.email || employee.employee_name;
        return (
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar
              src={employee.profile_picture || undefined}
              sx={{
                width: 34,
                height: 34,
                fontSize: 14,
                bgcolor: avatarColorOf(avatarSeed),
                color: "#ffffff",
              }}
            >
              {employee.employee_name?.charAt(0)}
            </Avatar>

            <Box>
              <Typography fontSize={14} fontWeight={600}>
                {employee.employee_name}
              </Typography>

              <Typography fontSize={12} color="#94a3b8">
                {employee.designation || "N/A"}
              </Typography>
            </Box>
          </Box>
        );
      }
    },
    {
      key: "email",
      label: "Email",
      width: "25%",
      render: (employee) => (
        <Typography fontSize={14} color="#475569">
          {employee.email}
        </Typography>
      )
    },
    {
      key: "department_name",
      label: "Department",
      width: "20%",
      render: (employee) => (
        <Chip
          label={employee.department_name || "N/A"}
          size="small"
          sx={{
            width: "100%",
            height: "auto",
            justifyContent: "flex-start",
            backgroundColor: "transparent",
            fontWeight: 500,
            fontSize: 12,
            color: "#2563eb",
            "& .MuiChip-label": {
              width: "100%",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              whiteSpace: "normal",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.2,
              textAlign: "left",
              paddingTop: "4px",
              paddingBottom: "4px",
            },
          }}
        />
      )
    },
    {
      key: "role_name",
      label: "Role",
      width: "12%",
      render: (employee) => (
        <Chip
          label={(employee.role_name || "member").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
          size="small"
          sx={{ fontWeight: 600, fontSize: 11, ...getRoleColor(employee.role_name) }}
        />
      )
    },
    {
      key: "manager_name",
      label: "Manager",
      width: "14%",
      render: (employee) => (
        <Typography fontSize={13} color="#475569">
          {employee.manager_name || "N/A"}
        </Typography>
      )
    },
    {
      key: "default_capacity_percent",
      label: "Default Cap.",
      width: "10%",
      align: "center",
      render: (employee) => (
        <Chip label={`${employee.default_capacity_percent ?? 100}%`} size="small"
          sx={{ fontWeight: 600, fontSize: 11, bgcolor: "#f5f3ff", color: "#7c3aed" }} />
      )
    },
    {
      key: "actions",
      label: "Actions",
      width: "12%",
      render: (employee) => (
        <Box display="flex" justifyContent="flex-end" gap={1}>
          <IconButton
            onClick={() => onEdit(employee)}
            sx={{ background: "#eff6ff", width: 32, height: 32 }}
          >
            <EditIcon fontSize="small" sx={{ color: "#2563eb" }} />
          </IconButton>

          <IconButton
            onClick={() => onDelete(employee)}
            sx={{ background: "#fef2f2", width: 32, height: 32 }}
          >
            <DeleteIcon fontSize="small" sx={{ color: "#ef4444" }} />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <DataTable
      columns={columns}
      rows={sortedEmployees}
      loading={loading}
      tableLayout="fixed"
      rowKey={(employee) => employee.employee_id}
      selectable
      selectedIds={selectedIds}
      onSelectionChange={onSelectionChange}
      rowsPerPage={50}
      emptyState={{
        icon: PeopleAltOutlinedIcon,
        title: "No employees found",
        subtitle: "Try changing search text or add a new employee to start building your team list."
      }}
    />
  );
}

export default EmployeeTable;
